import type { PortalRole } from '@/types';

/** Permission string format: resource:action — mirrored from backend constants. */
export type Permission =
  | 'dashboard:view'
  | 'dashboard:platform'
  | 'tenants:view'
  | 'tenants:edit'
  | 'users:view'
  | 'users:edit'
  | 'users:platform'
  | 'salesforce:view'
  | 'salesforce:edit'
  | 'telephony:view'
  | 'telephony:edit'
  | 'agents:view'
  | 'agents:edit'
  | 'agents:sync'
  | 'validation:view'
  | 'validation:run'
  | 'health:view'
  | 'calls:view'
  | 'calls:all'
  | 'audit:view'
  | 'audit:all'
  | 'login_history:view'
  | 'login_history:all'
  | 'credentials:view'
  | 'credentials:edit'
  | 'settings:view'
  | 'settings:edit'
  | 'platform:view'
  | 'platform:edit'
  | 'account:edit'
  | 'setup:view'
  | 'setup:edit';

export type RolePermissions = Record<PortalRole, Permission[]>;

const TENANT_ADMIN_PERMISSIONS: Permission[] = [
  'dashboard:view',
  'users:view',
  'users:edit',
  'salesforce:view',
  'salesforce:edit',
  'telephony:view',
  'telephony:edit',
  'agents:view',
  'agents:edit',
  'agents:sync',
  'validation:view',
  'validation:run',
  'health:view',
  'calls:view',
  'audit:view',
  'login_history:view',
  'credentials:view',
  'credentials:edit',
  'settings:view',
  'settings:edit',
  'account:edit',
  'setup:view',
  'setup:edit',
];

const USER_PERMISSIONS: Permission[] = [
  'dashboard:view',
  'salesforce:view',
  'telephony:view',
  'calls:view',
  'login_history:view',
  'account:edit',
  'settings:view',
];

const VOICERA_ADMIN_PERMISSIONS: Permission[] = [
  ...new Set<Permission>([
    ...TENANT_ADMIN_PERMISSIONS,
    'dashboard:platform',
    'tenants:view',
    'tenants:edit',
    'users:platform',
    'calls:all',
    'audit:all',
    'login_history:all',
    'platform:view',
    'platform:edit',
  ]),
];

export const ROLE_PERMISSIONS: RolePermissions = {
  VOICERA_ADMIN: VOICERA_ADMIN_PERMISSIONS,
  TENANT_ADMIN: TENANT_ADMIN_PERMISSIONS,
  USER: USER_PERMISSIONS,
};

export function permissionsForRole(role: PortalRole): Permission[] {
  return ROLE_PERMISSIONS[role] ? [...ROLE_PERMISSIONS[role]] : [];
}

export function roleHasPermission(role: PortalRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function isPermission(value: string): value is Permission {
  return Object.values(ROLE_PERMISSIONS)
    .flat()
    .includes(value as Permission);
}
